import {
  FEATURE_ACCESS_ENTITLEMENT_KEYS,
  resolveVisibleFeatures,
} from "./featureAccessRegistry";
import { FeatureAccessTable } from "./FeatureAccessTable";

import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import {
  useGetAdminV2FeatureReadinessQuery,
  useGetAdminV2MeQuery,
} from "@/store/api/baseApi";
import { VNextNotice } from "@/vnext/primitives";
import { ProductWorkspaceChrome } from "@/vnext/routes/product/ProductWorkspaceChrome";

/**
 * Feature flags console.
 *
 * @remarks
 * Despite the "Feature flags" label (its long-standing nav slot), this page
 * manages entitlement-backed feature rollout: a table with one row per feature.
 * Each row toggles the feature on for every organisation (behind a confirmation
 * modal) and expands to grant/exclude specific organisations. Writes go to the
 * `entitlements` table — the same store the runtime gates read — not the
 * `feature_flags` table used by the revenue control plane.
 *
 * Managed features live in `featureAccessRegistry.ts`. Each has a readiness
 * status: normal admins (`entitlements.manage`) see only ready features; dev
 * staff (`featureflags.manage`) see every feature with a readiness toggle column
 * and can mark each ready or not ready without a deploy.
 */
export default function FeatureFlagsRoute() {
  const { data: meData } = useGetAdminV2MeQuery();
  const { hasPermission } = useAdminPermissions(meData?.permissionKeys);
  const canManage = hasPermission("entitlements.manage");
  const isDevStaff = hasPermission("featureflags.manage");

  const readinessQuery = useGetAdminV2FeatureReadinessQuery({
    entitlementKeys: FEATURE_ACCESS_ENTITLEMENT_KEYS,
  });
  const readiness = readinessQuery.data?.readiness ?? {};
  const features = resolveVisibleFeatures({ readiness, isDevStaff });

  return (
    <ProductWorkspaceChrome
      subtitle="Turn features on per site — production or staging — for everyone or for specific organisations."
      title="Feature flags"
    >
      <div className="vnext-stack">
        {!canManage ? (
          <VNextNotice tone="warning">
            You can view feature access, but changing it needs the entitlements
            management permission.
          </VNextNotice>
        ) : null}
        {readinessQuery.isError ? (
          <VNextNotice tone="danger">
            Feature readiness could not be loaded, so newly readied features may
            be missing. Refresh to try again.
          </VNextNotice>
        ) : null}
        <FeatureAccessTable
          canManage={canManage}
          features={features}
          isDevStaff={isDevStaff}
          readiness={readiness}
        />
      </div>
    </ProductWorkspaceChrome>
  );
}
